import { useCallback, useEffect, useMemo, useState } from "react"

/**
 * @template  {string} T
 * @template {string} [K='collection']
 * @param  {T} collectionURL
 * @param {{key: K, sendSiteId: boolean}} options
 */

export default function hookCollectionStoreFactory(collectionURL, { sendSiteId = true, key = 'collection' }) {
  return ({ fetch, siteSelected }) => {
    const [item, setItem] = useState(null)
    const [preview, setPreview] = useState(null)

    const reset = useCallback(() => {
      setItem(null)
    }, [])

    useEffect(() => () => {
      reset()
    }, [reset])

    const addSiteId = useCallback((payload = {}) => {
      if (siteSelected && sendSiteId) {
        const { key, id } = siteSelected
        if (!payload[key]) {
          payload[key] = id
        }
      }

      return payload
    }, [siteSelected])

    const deleteEntry = useCallback(async (entry, params = {}) => await fetch({
      method: "delete",
      url: `${collectionURL}/${entry.id}`,
      data: addSiteId(entry),
      params,
    }), [fetch, addSiteId])

    const fetchById = useCallback(async (id, params = {}) => {
      const item = await fetch({
        url: `${collectionURL}/${id}`,
        params: addSiteId(params),
      })
      setItem(item)
      return item
    }, [fetch, addSiteId])

    const save = useCallback(async (payload, params = {}) => {
      const method = payload.id ? 'put' : 'post';
      return await fetch({
        method,
        url: `${collectionURL}/${payload.id || ''}`,
        data: addSiteId(payload),
        params,
      })
    }, [fetch, addSiteId])

    return useMemo(() => {
      const store = {
        collectionURL,
        setItem, item, reset, deleteEntry, fetchById, save,

        history: {
          preview,
          setPreview,
          reset: () => setPreview(null),
        },

        useLoad: (id, params) => {
          const loadData = useCallback(async () => {
            if (id) {
              await fetchById(id, params)
            } else {
              setItem({})
            }
          }, [id, params])

          useEffect(() => {
            loadData()
          }, [loadData])

          return loadData
        },

        useSubmit: () => useCallback(async payload => save(payload), []),
      }

      /** @type {{[x in K]: store}} */
      const result = {
        [key]: store,
      }
      return result
    }, [item, preview, reset, deleteEntry, fetchById, save])
  }
}
